import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Building2, Loader2, Sparkles,
  Hotel, Users,
  Edit2Icon
} from 'lucide-react';
import { CompsetContext, useCompset } from '@/contexts/CompsetContext';
import { usePlans } from '@/contexts/PlanRulesContext';
import { PLAN_NAME_MAPPER } from '@/constants';
import { IN_APP_ROUTES } from '@/constants/urls';
import DashboardError from '../error/DashboardError';



export default function CompsetSettings() {

  const navigate = useNavigate();
  // const { compsets, isErrorCompsets, isLoadingCompsets, setClient, setCompHotels } = useCompset()
  const { planRules, maxCompSets, isLoadingPlanRules, maxCompetitorsPerCompset } = usePlans();
  const [isEditMode, setIsEditMode] = useState(false)


  //added
  const { compsets, isErrorCompsets, isLoadingCompsets, setClient, setCompHotels, setEditingCompset } = useCompset()

  const onEditCompset = (compset) => {
    // Store compset id + name for the edit API call
    setEditingCompset({
      compset_id: compset.compset_id,
      compset_name: compset.compset_name,
    });

    const yourHotel = { ...compset.your_hotel, removable: false };
    setClient(yourHotel);

    const filtered = compset.competitors.map(comp => ({
      ...comp,
      removable: false   // existing competitors are locked
    }));
    setCompHotels(filtered);

    navigate(IN_APP_ROUTES.ADD_COMPSET);
  };
  //

  if (isLoadingCompsets || isLoadingPlanRules) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="compset-settings-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading compset data...</p>
        </div>
      </div>
    );
  }

  if (isErrorCompsets) {
    return <DashboardError
      title='Unable to load CompSets'
      section='Compsets'
    />
  }

  // No compset configured yet
  if (!compsets?.user_compsets?.length) {
    return (
      <div className="space-y-6" data-testid="compset-settings-empty">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Competitive Set</h2>
          <p className="text-muted-foreground">Configure the hotels you want to track</p>
        </div>

        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-primary/10 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Compset Configured</h3>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              Set up your competitive set to start tracking competitor prices and market insights.
            </p>
            <Button onClick={() => navigate('/compset-setup')} data-testid="setup-compset-btn">
              <Sparkles className="h-4 w-4 mr-2" />
              Setup Compset
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="space-y-6 min-h-[75vh]" data-testid="compset-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Competitive Set</h2>
          <p className="text-muted-foreground">Manage your hotel and competitors</p>
        </div>
        <div className="flex items-center gap-2">
          {/* <Badge variant={planRules?.plan_info?.plan_name?.toLowerCase() === PLAN_NAME_MAPPER.trial ? 'secondary' : 'default'} className="gap-1">
            {planRules?.plan_info?.plan_name?.toLowerCase() === PLAN_NAME_MAPPER.basic && <Crown className="h-3 w-3" />}
            {planRules?.plan_info?.plan_name} Plan
          </Badge>
          <span className="text-sm text-muted-foreground">
            • Up to {maxCompetitorsPerCompset} competitors
          </span> */}
          {
            compsets?.user_compsets?.length < maxCompSets && (
              // CompsetSettings.jsx
              <Button onClick={() => navigate(IN_APP_ROUTES.CREATE_COMPSET)}>
                Add CompSet
              </Button>
            )
          }
        </div>
      </div>

      {/* Compset Name Display (View Mode) */}
      {!isEditMode && compsets?.compset_name && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Compset Name</p>
                  <p className="text-xl font-semibold text-primary" data-testid="compset-name-display">{compsetData.compset_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {
          compsets?.user_compsets?.map(compset => {
            return (
              <>
                <Card className="border-primary/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 justify-between">
                      <div className='flex items-center gap-2'>
                        <Hotel className="h-4 w-4 text-primary" />
                        {compset?.compset_name}
                      </div>
                      <div>
                        {compset?.competitors?.length < maxCompetitorsPerCompset && (
                          <button onClick={() => onEditCompset(compset)} className='cursor-pointer'>
                            <Edit2Icon size={15} />
                          </button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {compset?.your_hotel ? (
                      <div className="space-y-3">
                        <div className="p-4 bg-primary/5 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-foreground">{compset?.your_hotel?.name}</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-3">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="line-clamp-2">{compset?.your_hotel?.address}</span>
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No hotel selected</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Competitors
                      </CardTitle>
                      <Badge variant="outline">
                        {compset?.competitors?.length || 0}/{maxCompetitorsPerCompset}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {compset?.competitors?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {compset?.competitors?.map((competitor, index) => (
                          <div
                            key={competitor?.google_place_id || index}
                            className="p-3 border border-border rounded-lg hover:border-primary/30 transition-colors"
                            data-testid={`view-competitor-${index}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted shrink-0">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-foreground">{competitor?.name}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="h-3 w-3 shrink-0" />
                                  <span className="line-clamp-1">{competitor?.address}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No competitors added</p>
                    )}
                  </CardContent>
                </Card>

              </>
            )
          })
        }
      </div>
    </div>
  );
}
